import CreateHtmlContainerModal from './components/modals/CreateHtmlContainerModal';
import { ContentCreateDropdown } from './components/ContentCreateDropdown';
import { ContentsFilter } from './components/ContentsFilter';
import { ContentsTable } from './components/ContentsTable';
import { CreateButton, useTable } from '@refinedev/antd';
import {
  CrudFilters,
  HttpError,
  useCan,
  useModal,
  useOne
} from '@refinedev/core';
import { HtmlContainersTable } from './components/HtmlContainersTable';
import { IGroup } from 'interfaces/epimetheus/groups';
import {
  ILibrary,
  ILibraryContent,
  ILibraryContentsFilter
} from 'interfaces/libraries';
import { PageHeader } from 'components/professor_area/PageHeader';
import { PlayCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { ShowLayout } from 'components/layout/layout/ShowLayout';
import { Tabs } from 'antd';
import { TagGroupsTable } from './components/TagGroupsTable';
import { normalizeFilterText } from 'pages/corporate_reports/utils/filters';
import { useParams } from 'react-router-dom';
import type { IHtmlContainer } from 'interfaces/prometheus';

export const LibrariesShow = () => {
  const { id } = useParams();
  const modal = useModal();
  const { data: canListContents } = useCan({
    resource: 'library_contents',
    action: 'list'
  });
  const { data: canCreateContents } = useCan({
    resource: 'library_contents',
    action: 'create'
  });
  const { data: canListTagGroups } = useCan({
    resource: 'library_tag_groups',
    action: 'list'
  });
  const { data: canCreateTagGroups } = useCan({
    resource: 'library_tag_groups',
    action: 'create'
  });
  const { data: canListHtmlContainers } = useCan({
    resource: 'library_html_containers',
    action: 'list'
  });
  const { data: canCreateHtmlContainers } = useCan({
    resource: 'library_html_containers',
    action: 'create'
  });

  const hasContentsAccess = !!canListContents?.can;
  const hasCreateContentsAccess = !!canCreateContents?.can;
  const hasTagGroupsAccess = !!canListTagGroups?.can;
  const hasCreateTagGroupsAccess = !!canCreateTagGroups?.can;
  const hasHtmlContainersAccess = !!canListHtmlContainers?.can;
  const hasCreateHtmlContainersAccess = !!canCreateHtmlContainers?.can;

  const { data } = useOne<ILibrary>({
    resource: 'prometheus/v2/libraries',
    id
  });

  const { tableProps: tagGroupsTableProps } = useTable<IGroup, HttpError>({
    resource: `prometheus/v2/libraries/${id}/tag_groups`,
    queryOptions: { enabled: !!id && hasTagGroupsAccess },
    meta: {
      libraryId: id
    }
  });

  const { tableProps: htmlContainersTableProps } = useTable<
    IHtmlContainer,
    HttpError
  >({
    resource: `prometheus/v2/libraries/${id}/html_containers`,
    queryOptions: { enabled: !!id && hasHtmlContainersAccess },
    meta: {
      libraryId: id
    }
  });

  const { tableProps, tableQueryResult, searchFormProps } = useTable<
    ILibraryContent,
    HttpError,
    ILibraryContentsFilter
  >({
    resource: `prometheus/v2/libraries/${id}/contents`,
    queryOptions: { enabled: !!id && hasContentsAccess },
    onSearch: ({ q, id, kind, tag_ids }: ILibraryContentsFilter) => {
      const filters: CrudFilters = [
        { field: 'q', operator: 'eq', value: q },
        { field: 'id', operator: 'eq', value: normalizeFilterText(id) },
        { field: 'kind', operator: 'eq', value: kind },
        { field: 'tag_id[]', operator: 'eq', value: tag_ids }
      ];

      return filters;
    }
  });

  const onFinish = () => {
    tableQueryResult.refetch();
  };
  const defaultActiveTab = hasContentsAccess
    ? 'contents'
    : hasTagGroupsAccess
    ? 'tag_groups'
    : 'html_containers';

  return (
    <ShowLayout>
      <PageHeader title={data?.data?.name} icon={PlayCircleOutlined} />

      <Tabs defaultActiveKey={defaultActiveTab} className="custom-tabs">
        {hasContentsAccess && (
          <Tabs.TabPane tab="Conteúdos" key="contents">
            {hasCreateContentsAccess && (
              <ContentCreateDropdown onFinish={onFinish} libraryId={id!} />
            )}
            <ContentsFilter searchFormProps={searchFormProps} libraryId={id!} />
            <ContentsTable tableProps={tableProps} />
          </Tabs.TabPane>
        )}

        {hasTagGroupsAccess && (
          <Tabs.TabPane tab="Grupos de tags" key="tag_groups">
            {hasCreateTagGroupsAccess && (
              <CreateButton
                resource="library_tag_groups"
                type="primary"
                meta={{ libraryId: id }}
                icon={<PlusOutlined />}
                className="!block ml-auto w-fit mb-4"
              >
                Novo grupo de tags
              </CreateButton>
            )}
            <TagGroupsTable tableProps={tagGroupsTableProps} />
          </Tabs.TabPane>
        )}

        {hasHtmlContainersAccess && (
          <Tabs.TabPane tab="Pastas de HTMLs" key="html_containers">
            {hasCreateHtmlContainersAccess && (
              <CreateButton
                onClick={() => modal.show()}
                meta={{ libraryId: id }}
                icon={<PlusOutlined />}
                type="primary"
                className="!block ml-auto w-fit mb-4"
              >
                Nova pasta de HTMLs
              </CreateButton>
            )}
            <HtmlContainersTable tableProps={htmlContainersTableProps} />
            {hasCreateHtmlContainersAccess && (
              <CreateHtmlContainerModal libraryId={id!} modal={modal} />
            )}
          </Tabs.TabPane>
        )}
      </Tabs>
    </ShowLayout>
  );
};
